<template>
  <v-card class="pa-6" flat>
    <v-form ref="form" v-model="valid">
      <!-- Title Section -->
      <h3 class="title mb-6">Edit Contractor</h3>
      
      <!-- Search Section -->
      <v-row>
        <v-col cols="12" md="8">
          <div class="text-subtitle-1 black--text mb-2">Search for contractor to edit</div>
          <Search
            label="Search by name"
            @selectedOID="fetchContractor" 
            @clearItem="handleClearSearch"
            endpoint="contractors"
            myParams="name"
          />
        </v-col>
      </v-row>

      <!-- Selected Contractor Card -->
      <v-card v-if="selectedContractorID && !fetchingContractor" class="mt-4 mb-6" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-1">Currently Editing:</div>
          <div class="text-body-1">{{ displayContractorName }}</div>
          <div class="text-caption">Contractor ID: {{ selectedContractorID }}</div>
        </v-card-text>
      </v-card>

      <!-- Loading Progress -->
      <v-progress-linear v-if="fetchingContractor" indeterminate color="secondary" class="mb-4"></v-progress-linear>

      <!-- Contractor Details Form -->
      <div v-if="selectedContractorID">
        <v-divider class="mb-6"></v-divider>
        
        <!-- Personal Details Section -->
        <h4 class="subtitle-1 mb-4">Personal Details</h4>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              v-model="title"
              :items="titles"
              label="Title"
              outlined
              dense
              :disabled="fetchingContractor"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="firstName"
              label="First Name"
              outlined
              dense
              :disabled="fetchingContractor"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="surname"
              label="Surname"
              outlined
              dense
              :disabled="fetchingContractor"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="email"
              label="Email"
              outlined
              dense
              type="email"
              :disabled="fetchingContractor"
              :rules="emailRules"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="mobile"
              label="Mobile Phone"
              outlined
              dense
              :disabled="fetchingContractor"
              :rules="phoneRules"
            />
          </v-col>
        </v-row>

        <!-- Address Section -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">Address</h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address1"
              label="Address Line 1"
              outlined
              dense
              :disabled="fetchingContractor"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address2"
              label="Address Line 2"
              outlined
              dense
              :disabled="fetchingContractor"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="address3"
              label="Address Line 3"
              outlined
              dense
              :disabled="fetchingContractor"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="address4"
              label="Address Line 4"
              outlined
              dense
              :disabled="fetchingContractor"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="postcode"
              label="Postcode"
              outlined
              dense
              :disabled="fetchingContractor"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>

        <!-- Bank Details Section (Read-only) -->
        <v-divider class="my-6"></v-divider>
        <h4 class="subtitle-1 mb-4">
          Bank Details
          <v-chip x-small class="ml-2" color="grey lighten-2">Read Only</v-chip>
        </h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="accountName"
              label="Account Name"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="account"
              label="Account Number"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="sortCode"
              label="Sort Code"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="statementRef"
              label="Statement Reference"
              outlined
              dense
              readonly
              filled
            />
          </v-col>
        </v-row>
        
        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-right">
            <v-btn
              text
              @click="handleClearSearch"
              :disabled="loading"
              class="mr-3"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="loading"
              :disabled="!valid || fetchingContractor"
              @click="saveContractor"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-2">mdi-account-hard-hat</v-icon>
        <p class="text-subtitle-1 grey--text mt-3">Search for a contractor above to start editing</p>
      </div>
    </v-form>

    <!-- Snackbar for notifications -->
    <Snackbar 
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :timeout="timeout"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
    />
  </v-card>
</template>

<script>
import { amplifyProxy } from '@/services/directApi'
import Search from '@/components/CloudSearch/Search'
import Snackbar from '@/components/Snackbars/TheSnackbar'

export default {
  components: {
    Search,
    Snackbar
  },
  data() {
    return {
      valid: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loading: false,
      fetchingContractor: false,
      selectedContractorID: '',
      title: '',
      firstName: '',
      surname: '',
      email: '',
      mobile: '',
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid',
      ],
      phoneRules: [
        v => !!v || 'Mobile phone is required',
        v => /^[\d\s+()-]+$/.test(v) || 'Phone number must be valid'
      ],
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      postcode: '',
      accountName: '',
      account: '',
      sortCode: '',
      statementRef: '',
      titles: ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof', 'Rev', 'Other'],
      rules: {
        required: value => !!value || 'Required field'
      }
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
    },

    handleClearSearch() {
      this.selectedContractorID = ''
      this.clearForm()
    },

    async fetchContractor(id) {
      this.selectedContractorID = id
      this.clearForm()
      this.fetchingContractor = true

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/contractors/${id}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data

        // Personal details
        this.title = data.ContractorName?.Title || ''
        this.firstName = data.ContractorName?.Forename || ''
        this.surname = data.ContractorName?.Surname || ''
        this.email = data.Email || ''
        this.mobile = data.Mobile || ''

        // Address
        this.address1 = data.Address?.Address1 || ''
        this.address2 = data.Address?.Address2 || ''
        this.address3 = data.Address?.Address3 || ''
        this.address4 = data.Address?.Address4 || ''
        this.postcode = data.Address?.Postcode || ''

        // Bank details
        this.accountName = data.AccountDetails?.AccountName || ''
        this.account = data.AccountDetails?.AccountNumber || ''
        this.sortCode = data.AccountDetails?.SortCode || ''
        this.statementRef = data.AccountDetails?.LegacyBacsReference || ''
      } catch (error) {
        console.error('Error fetching contractor:', error)
        this.snackbar = true
        this.snackbarText = 'Error fetching contractor details: ' + (error.response?.data?.message || error.message)
        this.snackbarColor = 'red'
      } finally {
        this.fetchingContractor = false
      }
    },

    clearForm() {
      this.title = ''
      this.firstName = ''
      this.surname = ''
      this.email = ''
      this.mobile = ''
      this.address1 = ''
      this.address2 = ''
      this.address3 = ''
      this.address4 = ''
      this.postcode = ''
      this.accountName = ''
      this.account = ''
      this.sortCode = ''
      this.statementRef = ''
    },

    async saveContractor() {
      this.loading = true

      const contractorObject = {
        ContractorName: {
          Title: this.title,
          Forename: this.firstName,
          Surname: this.surname
        },
        Email: this.email,
        Mobile: this.mobile,
        AccountDetails: {
          AccountName: this.accountName,
          AccountNumber: this.account,
          SortCode: this.sortCode,
          LegacyBacsReference: this.statementRef
        },
        Address: {
          Address1: this.address1,
          Address2: this.address2,
          Address3: this.address3,
          Address4: this.address4,
          Postcode: this.postcode
        }
      }

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/contractors/${this.selectedContractorID}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        await amplifyProxy.patch(endpoint, contractorObject, options)

        this.snackbar = true
        this.snackbarText = 'Contractor update successful'
        this.snackbarColor = 'green'
        await this.fetchContractor(this.selectedContractorID)
      } catch (error) {
        this.snackbar = true
        this.snackbarText = 'ERROR: ' + (error.response?.data || error.message)
        this.snackbarColor = 'red'
        await this.fetchContractor(this.selectedContractorID)
      } finally {
        this.loading = false
      }
    }
  },
  computed: {
    displayContractorName() {
      if (!this.firstName && !this.surname) return 'No contractor selected'
      return `${this.title} ${this.firstName} ${this.surname}`.trim()
    }
  }
}
</script>